"use client";
import styles from "./homePageBanner.module.css";
import React, { useEffect, useRef, useState } from "react";
import Container from "../container";
import Icon from "@/lib/icon";
import {
  IconSearch,
  IconMapPin,
  IconX,
  IconBuildingStore,
} from "@/lib/icons";
import { StoreBadge } from "../mobile-banner/mobilebanner";
import Image from "next/image";
import AppImage from "../common/AppImage";
import LocationSelector from "../LocationSelector/locationSelector";
import { useRouter } from "next/navigation";
import { ENDPOINTS, getApiV2Url } from "@/constants/endpoint";
import { DEV_AUTH_HEADERS } from "@/lib/devHeaders";
import Link from "next/link";
import ConvertedTime from "../convertedtime";
import { sanitizeSearchInput } from "@/lib/validations";
import organizerPlaceholder from "@/images/organizer_placeholder.png";
import artistPlaceholder from "@/components/Artist/MEN.png";

const getMediaUrl = (media) => {
  if (!media) return null;
  if (typeof media === "string") return media;
  if (Array.isArray(media)) return media[0]?.url || media[0]?.thumbnail || null;
  return media.url || media.thumbnail || null;
};

// Entity image with a placeholder fallback, same as the search page
const PlaceholderThumb = ({ src, alt, placeholder }) => {
  const [imgError, setImgError] = useState(false);
  return (
    <Image
      unoptimized
      src={src && !imgError ? src : placeholder}
      alt={alt}
      fill
      sizes="44px"
      style={{ objectFit: "cover" }}
      onError={() => setImgError(true)}
    />
  );
};

// ── Color helpers ────────────────────────────────────────────

const buildBannerBackground = (colorValue) => {
  const fallback =
    "linear-gradient(360deg, #e8f4fd 0%, #f0f8ff 50%, #ffffff 100%)";
  if (!colorValue) return fallback;

  let r, g, b;

  if (typeof colorValue === "string") {
    let hex = colorValue.trim().replace(/^#/, "");
    if (hex.length === 3)
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    if (!/^[0-9a-f]{6}$/i.test(hex)) return fallback;
    const num = parseInt(hex, 16);
    r = (num >> 16) & 255;
    g = (num >> 8) & 255;
    b = num & 255;
  } else if (colorValue?.metaColor?.isValid) {
    ({ r, g, b } = colorValue.metaColor);
  } else {
    return fallback;
  }

  return `linear-gradient(
    360deg,
    rgba(${r}, ${g}, ${b}, 0.35) 0%,
    rgba(${r}, ${g}, ${b}, 0.07) 40%,
    rgba(${r}, ${g}, ${b}, 0.02) 75%,
    #ffffff 100%
  )`;
};

// ── Main component ───────────────────────────────────────────

const HomePageBanner = ({ data }) => {
  if (!data) return null;

  const {
    icons,
    headingLines: cmsHeadingLines,
    subheadingLines: cmsSubheadingLines,
    heading,
    subheading,
    right_top_illustration_banner,
    right_bottom_illustration_banner,
    left_top_illustration_banner,
    left_bottom_illustration_banner,
    mobile_left_illustration_banner,
    mobile_right_illustration_banner,
    background_color,
    play_store_link,
    app_store_link,
    application_tagline,
    enable_location,
    enable_search,
  } = data;

  const normalizedHeadingLines =
    Array.isArray(cmsHeadingLines) && cmsHeadingLines.length > 0
      ? cmsHeadingLines
          .map((l) =>
            typeof l === "string"
              ? l.trim()
              : String(l?.text || l?.label || l?.title || "").trim(),
          )
          .filter(Boolean)
      : typeof heading === "string" && heading.trim()
        ? [heading.trim()]
        : [];

  const normalizedSubheadingLines =
    Array.isArray(cmsSubheadingLines) && cmsSubheadingLines.length > 0
      ? cmsSubheadingLines
          .map((l) =>
            typeof l === "string"
              ? l.trim()
              : String(l?.text || l?.label || l?.title || "").trim(),
          )
          .filter(Boolean)
      : typeof subheading === "string" && subheading.trim()
        ? [subheading.trim()]
        : [];

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [activeIconIndex, setActiveIconIndex] = useState(0);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const router = useRouter();

  useEffect(() => {
    if (!icons?.length) return;
    const interval = setInterval(() => {
      setActiveIconIndex((prev) => (prev + 1) % icons.length);
    }, 2800);
    return () => clearInterval(interval);
  }, [icons?.length]);

  // Debounced live search
  useEffect(() => {
    const q = query.trim();

    if (!q) {
      setResults([]);
      setDropdownOpen(false);
      setSearching(false);
      return;
    }

    setSearching(true);
    clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      try {
        const url = `${getApiV2Url(ENDPOINTS.SEARCH)}?search=${encodeURIComponent(q)}`;
        const res = await fetch(url, {
          method: "GET",
          credentials: "include",
          headers: { ...DEV_AUTH_HEADERS },
        });
        const json = await res.json();
        const list = Array.isArray(json)
          ? json
          : Array.isArray(json?.data)
            ? json.data
            : [];
        setResults(list);
        setDropdownOpen(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  useEffect(() => {
    if (data?.search_placeholders && data.search_placeholders.length > 1) {
      const interval = setInterval(() => {
        setPlaceholderIndex(
          (prev) => (prev + 1) % data.search_placeholders.length,
        );
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [data?.search_placeholders]);

  const placeholder =
    data?.search_placeholders?.[placeholderIndex]?.content || "search...";

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = () => {
    setDropdownOpen(false);
    const q = query.trim();
    router.push(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
    if (e.key === "Escape") setDropdownOpen(false);
  };

  const handleResultClick = () => {
    setDropdownOpen(false);
    setQuery("");
  };

  const clearQuery = () => {
    setQuery("");
    setResults([]);
    setDropdownOpen(false);
  };

  const hasText =
    normalizedHeadingLines.length > 0 ||
    normalizedSubheadingLines.length > 0 ||
    Boolean(heading) ||
    Boolean(subheading);
  const hasDecorations = Boolean(
    left_top_illustration_banner?.[0] ||
    left_bottom_illustration_banner?.[0] ||
    right_top_illustration_banner?.[0] ||
    right_bottom_illustration_banner?.[0] ||
    mobile_left_illustration_banner?.[0] ||
    mobile_right_illustration_banner?.[0],
  );
  if (!hasText && !hasDecorations && !enable_search && !enable_location) {
    return null;
  }

  const bannerBg = buildBannerBackground(background_color);

  const checkIsValidLink = (link) => {
    if (!link) return false;
    if (typeof link === "string") return link.trim().length > 0;
    if (Array.isArray(link)) {
      if (link.length === 0) return false;
      const first = link[0];
      if (typeof first === "string") return first.trim().length > 0;
      return !!first?.url;
    }
    return !!link.url;
  };

  const isPlayStoreValid = checkIsValidLink(play_store_link);
  const isAppStoreValid = checkIsValidLink(app_store_link);

  return (
    <section className={styles.banner} style={{ background: bannerBg }}>
      {/* Decoration Layer */}
      <div className={styles.decorations}>
        {left_top_illustration_banner?.[0] && (
          <AppImage
            src={left_top_illustration_banner}
            width={420}
            height={420}
            className={styles.leftTop}
            alt={
              left_top_illustration_banner[0]?.alt_name ||
              "Top Left Illustration"
            }
            priority
            sizes="(max-width: 768px) 30vw, 420px"
          />
        )}
        {left_bottom_illustration_banner?.[0] && (
          <AppImage
            src={left_bottom_illustration_banner}
            width={500}
            height={500}
            className={styles.leftBottom}
            alt={
              left_bottom_illustration_banner[0]?.alt_name ||
              "Bottom Left Illustration"
            }
            loading="lazy"
            decoding="async"
            sizes="(max-width: 768px) 0px, 500px"
          />
        )}
        {right_top_illustration_banner?.[0] && (
          <AppImage
            src={right_top_illustration_banner}
            width={420}
            height={420}
            className={styles.rightTop}
            alt={
              right_top_illustration_banner[0]?.alt_name ||
              "Top Right Illustration"
            }
            loading="lazy"
            decoding="async"
            sizes="(max-width: 768px) 30vw, 420px"
          />
        )}
        {right_bottom_illustration_banner?.[0] && (
          <AppImage
            src={right_bottom_illustration_banner}
            width={500}
            height={500}
            className={styles.rightBottom}
            alt={
              right_bottom_illustration_banner[0]?.alt_name ||
              "Bottom Right Illustration"
            }
            loading="lazy"
            decoding="async"
            sizes="(max-width: 768px) 0px, 500px"
          />
        )}

        {/* Mobile Banners — CSS (object-fit: contain, width: auto,
            height: 30dvh) already renders these at their true aspect
            ratio regardless of these props, but Next uses width/height
            to reserve space before CSS applies, and Lighthouse's aspect
            ratio audit compares these against each image's actual
            uploaded dimensions. These illustration banners are uploaded
            close to a ~1.14:1 ratio (e.g. 968x852, 367x322), not square —
            420x420 was a bigger mismatch than necessary. */}
        {mobile_left_illustration_banner?.[0] && (
          <AppImage
            src={mobile_left_illustration_banner}
            width={420}
            height={368}
            className={styles.mobileLeft}
            alt={
              mobile_left_illustration_banner[0]?.alt_name ||
              "Mobile Left Illustration"
            }
            loading="lazy"
            decoding="async"
            sizes="(max-width: 768px) 28vw, 0px"
          />
        )}
        {mobile_right_illustration_banner?.[0] && (
          <AppImage
            src={mobile_right_illustration_banner}
            width={420}
            height={368}
            className={styles.mobileRight}
            alt={
              mobile_right_illustration_banner[0]?.alt_name ||
              "Mobile Right Illustration"
            }
            loading="lazy"
            decoding="async"
            sizes="(max-width: 768px) 28vw, 0px"
          />
        )}
      </div>

      {/* Content */}
      <Container>
        <div className={styles.content}>
          {normalizedHeadingLines.length > 0 && (
            <h1 className={styles.headingText}>
              {normalizedHeadingLines.map((line, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <br />}
                  {line}
                </React.Fragment>
              ))}
            </h1>
          )}

          {normalizedSubheadingLines.length > 0 && (
            <p className={styles.subheadingText}>
              {normalizedSubheadingLines.map((line, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <br />}
                  {line}
                </React.Fragment>
              ))}
            </p>
          )}

          {(enable_search || enable_location) && (
            <div className={styles.searchRow} ref={wrapperRef}>
              {/* Pill: input + divider + location */}
              <div className={styles.searchPill}>
                {enable_search && (
                  <div className={styles.searchInputWrap}>
                    <IconSearch size={15} color="var(--color-grey)" />
                    <input
                      className={styles.searchInput}
                      placeholder={placeholder}
                      value={query}
                      onChange={(e) =>
                        setQuery(sanitizeSearchInput(e.target.value))
                      }
                      onKeyDown={handleKeyDown}
                      onFocus={() =>
                        results.length > 0 && setDropdownOpen(true)
                      }
                      autoComplete="off"
                    />
                    {query && (
                      <button
                        className={styles.clearBtn}
                        onClick={clearQuery}
                        aria-label="Clear search"
                        type="button"
                      >
                        <IconX size={14} color="var(--color-grey)" />
                      </button>
                    )}
                  </div>
                )}

                {enable_search && enable_location && (
                  <div className={styles.pillDivider} />
                )}

                {enable_location && (
                  <div className={styles.locationWrap}>
                    <LocationSelector hideLabel fromBanner={true} />
                  </div>
                )}
              </div>

              {/* Separate search button */}
              <button
                className={styles.searchBtn}
                onClick={handleSearch}
                aria-label="Search events"
                type="button"
              >
                <IconSearch size={18} color="#021526" />
              </button>

              {/* Live results dropdown */}
              {dropdownOpen && (
                <div className={styles.dropdown}>
                  {searching ? (
                    <div className={styles.dropdownState}>
                      <span className={styles.dropdownStateText}>
                        Searching…
                      </span>
                    </div>
                  ) : results.length === 0 ? (
                    <div className={styles.dropdownState}>
                      <span className={styles.dropdownStateText}>
                        No results found for &ldquo;{query}&rdquo;
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className={styles.dropdownHeader}>
                        <span>
                          {results.length} result
                          {results.length !== 1 ? "s" : ""}
                        </span>
                        <button
                          className={styles.seeAllBtn}
                          onClick={handleSearch}
                          type="button"
                        >
                          See all
                        </button>
                      </div>
                      <ul className={styles.resultList}>
                        {results.slice(0, 8).map((item, idx) => {
                          const itemType = item.type || "event";
                          const rawSlug = (item.slug || "").trim();
                          let itemHref = rawSlug.startsWith("/")
                            ? rawSlug
                            : `/${rawSlug}`;
                          if (
                            itemType === "artist" &&
                            !rawSlug.startsWith("/") &&
                            !rawSlug.startsWith("artist/")
                          ) {
                            itemHref = `/artist/${rawSlug}`;
                          } else if (
                            itemType === "venue" &&
                            !rawSlug.startsWith("/") &&
                            !rawSlug.startsWith("venue/")
                          ) {
                            itemHref = `/venue/${rawSlug}`;
                          }

                          const isOrganizer =
                            itemType === "organizer" ||
                            itemType === "organiser";

                          if (isOrganizer) {
                            if (
                              !rawSlug.startsWith("/") &&
                              !rawSlug.startsWith("organizer/")
                            ) {
                              itemHref = `/organizer/${rawSlug}`;
                            }
                            const organizerLoc = [
                              item.address?.city,
                              item.address?.state,
                              item.address?.country,
                            ]
                              .filter(Boolean)
                              .join(", ");
                            const eventsCount = item.public_events_count ?? 0;

                            return (
                              <li key={item.slug || `organizer-${idx}`}>
                                <Link
                                  href={itemHref}
                                  className={styles.resultItem}
                                  onClick={handleResultClick}
                                >
                                  <div className={styles.resultThumb}>
                                    <PlaceholderThumb
                                      src={getMediaUrl(
                                        item.logo ||
                                          item.profile_pic ||
                                          item.image ||
                                          item.feature_image,
                                      )}
                                      alt={item.name || "Organizer"}
                                      placeholder={organizerPlaceholder}
                                    />
                                  </div>
                                  <div className={styles.resultInfo}>
                                    <span className={styles.resultName}>
                                      {item.name}
                                    </span>
                                    <div className={styles.resultMetaRow}>
                                      <span
                                        className={`${styles.resultBadge} ${styles.resultOrganizerBadge}`}
                                      >
                                        Organizer
                                      </span>
                                      {organizerLoc && (
                                        <span
                                          className={styles.resultSubMeta}
                                          title={organizerLoc}
                                        >
                                          <IconMapPin size={11} />
                                          {organizerLoc}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {eventsCount > 0 && (
                                    <span className={styles.resultEventsCount}>
                                      {eventsCount}{" "}
                                      {eventsCount === 1 ? "Event" : "Events"}
                                    </span>
                                  )}
                                </Link>
                              </li>
                            );
                          }

                          let itemImg = null;
                          if (itemType === "artist") {
                            const pic = item.profile_pic;
                            itemImg =
                              typeof pic === "string"
                                ? pic
                                : Array.isArray(pic)
                                  ? pic[0]?.url || pic[0]?.thumbnail
                                  : pic?.url || pic?.thumbnail || null;
                          } else {
                            const img = item.image;
                            itemImg =
                              typeof img === "string"
                                ? img
                                : Array.isArray(img)
                                  ? img[0]?.url || img[0]?.thumbnail
                                  : img?.url || img?.thumbnail || null;
                          }

                          if (itemType === "artist") {
                            return (
                              <li key={item.slug || `artist-${idx}`}>
                                <Link
                                  href={itemHref}
                                  className={styles.resultItem}
                                  onClick={handleResultClick}
                                >
                                  <div
                                    className={`${styles.resultThumb} ${styles.resultThumbAvatar}`}
                                  >
                                    <PlaceholderThumb
                                      src={itemImg}
                                      alt={item.name || "Artist"}
                                      placeholder={artistPlaceholder}
                                    />
                                  </div>
                                  <div className={styles.resultInfo}>
                                    <span className={styles.resultName}>
                                      {item.name}
                                    </span>
                                    <div className={styles.resultMetaRow}>
                                      <span
                                        className={`${styles.resultBadge} ${styles.resultArtistBadge}`}
                                      >
                                        Artist
                                      </span>
                                      {item.category && (
                                        <span className={styles.resultSubMeta}>
                                          {item.category}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </Link>
                              </li>
                            );
                          }

                          if (itemType === "venue") {
                            const venueLoc = [
                              item.address?.line1,
                              item.address?.city,
                            ]
                              .filter(Boolean)
                              .join(", ");

                            return (
                              <li key={item.slug || `venue-${idx}`}>
                                <Link
                                  href={itemHref}
                                  className={styles.resultItem}
                                  onClick={handleResultClick}
                                >
                                  <div className={styles.resultThumb}>
                                    {itemImg ? (
                                      <Image
                                        unoptimized
                                        src={itemImg}
                                        alt={item.name || "Venue"}
                                        fill
                                        sizes="44px"
                                        style={{ objectFit: "cover" }}
                                      />
                                    ) : (
                                      <IconBuildingStore
                                        size={22}
                                        color="#94a3b8"
                                      />
                                    )}
                                  </div>
                                  <div className={styles.resultInfo}>
                                    <span className={styles.resultName}>
                                      {item.name}
                                    </span>
                                    <div className={styles.resultMetaRow}>
                                      <span
                                        className={`${styles.resultBadge} ${styles.resultVenueBadge}`}
                                      >
                                        Venue
                                      </span>
                                      {venueLoc && (
                                        <span className={styles.resultSubMeta}>
                                          <IconMapPin size={11} />
                                          {venueLoc}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </Link>
                              </li>
                            );
                          }

                          // Event
                          return (
                            <li key={item.id || item.slug || `event-${idx}`}>
                              <Link
                                href={itemHref}
                                className={styles.resultItem}
                                onClick={handleResultClick}
                              >
                                <div className={styles.resultThumb}>
                                  {itemImg ? (
                                    <Image
                                      unoptimized
                                      src={itemImg}
                                      alt={item.name || "Event"}
                                      fill
                                      sizes="44px"
                                      style={{ objectFit: "cover" }}
                                    />
                                  ) : (
                                    <span
                                      className={styles.resultThumbFallback}
                                    >
                                      {item.name?.[0] || "E"}
                                    </span>
                                  )}
                                </div>

                                <div className={styles.resultInfo}>
                                  <span className={styles.resultName}>
                                    {item.name}
                                  </span>
                                  <span className={styles.resultMeta}>
                                    {item.venue?.city && (
                                      <span className={styles.resultMetaItem}>
                                        <IconMapPin size={11} />
                                        {item.venue.city}
                                      </span>
                                    )}
                                    {item.start_date && (
                                      <ConvertedTime
                                        time={item?.start_time}
                                        timezone={item?.timezone}
                                        date={item?.start_date}
                                      />
                                    )}
                                  </span>
                                </div>

                                <span className={styles.resultPrice}>
                                  {item.price ||
                                    (item.amount === 0 ? "Free" : null)}
                                </span>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {!!icons?.length && (
            <div className={styles.features}>
              {icons.map((item, index) => (
                <div
                  key={index}
                  className={`${styles.feature} ${
                    index === activeIconIndex ? styles.featureActiveMobile : ""
                  }`}
                >
                  <Icon value={item.icon} size={20} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          )}

          {(isPlayStoreValid || isAppStoreValid) && (
            <div className={styles.downloadSection}>
              {application_tagline && (
                <p className={styles.tagline}>{application_tagline}</p>
              )}
              <div className={styles.storeButtons}>
                {isPlayStoreValid && (
                  <StoreBadge
                    btn={Array.isArray(play_store_link) ? play_store_link[0] : play_store_link}
                    iconKey="google-play-fill"
                    variant="light"
                  />
                )}
                {isAppStoreValid && (
                  <StoreBadge
                    btn={Array.isArray(app_store_link) ? app_store_link[0] : app_store_link}
                    iconKey="apple-fill"
                    variant="light"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

export default HomePageBanner;
